import { ROUTES } from '../../../../global';
import i18n from '../../../../global/utils/localization';
import { SortDirection } from '../../../../shared/utils/constants';
import { SelectValueSliderProps } from '../../../components/SelectValueSlider';
import {
  ConditionalEventsSortFilterConfig,
  ConditionalEventType,
  EventTypeFilters,
} from '../types';

const MORE_THAN_VALUE_KEY = ROUTES.reporting.conditionalEvents.searchParams
  .moreThanValue as keyof EventTypeFilters;

const DURATION_KEY = ROUTES.reporting.conditionalEvents.searchParams
  .duration as keyof EventTypeFilters;

const RESOLVE_KEY = ROUTES.reporting.conditionalEvents.searchParams
  .resolve as keyof EventTypeFilters;

export const FILTERS_CONTAINER_WIDTH = 600;

export const SORT_FILTER: ConditionalEventsSortFilterConfig = {
  defaultSortBy: 'start',
  defaultSortDirection: SortDirection.DESC,
};

export const EVENT_TYPE_OPTIONS: {
  id: ConditionalEventType;
  label: string;
  disabled?: boolean;
}[] = [
  {
    id: ConditionalEventType.STOP_TIME,
    label: i18n.t('reports.conditionalEventsReport.stop'),
  },
  {
    id: ConditionalEventType.OVER_SPEEDING,
    label: i18n.t('reports.conditionalEventsReport.overspeeding'),
  },

  {
    id: ConditionalEventType.TEMPERATURE,
    label: i18n.t('reports.conditionalEventsReport.temperature'),
    disabled: true,
  },

  {
    id: ConditionalEventType.RPM_LIMIT,
    label: i18n.t('reports.conditionalEventsReport.rpmLimit'),
    disabled: true,
  },
];

export const EVENT_TYPE_FILTERS: Record<
  ConditionalEventType,
  {
    [key in keyof Partial<EventTypeFilters>]: Omit<
      SelectValueSliderProps,
      'onChange' | 'value' | 'marks'
    > & {
      id: number;
      filterKey: keyof EventTypeFilters;
      defaultValue: number;
      marks: { valueInSeconds: number; label: string; value: number }[];
    };
  }
> = {
  [ConditionalEventType.STOP_TIME]: {
    [DURATION_KEY]: {
      id: 1,
      filterKey: DURATION_KEY,
      defaultValue: 20,
      min: 0,
      max: 90,
      step: 10,
      primaryText: i18n.t('reports.conditionalEventsReport.waitMoreThan'),
      marks: [
        {
          value: 0,
          valueInSeconds: 120,
          label: '2m',
        },
        {
          value: 10,
          valueInSeconds: 300,
          label: '5m',
        },
        {
          value: 20,
          valueInSeconds: 600,
          label: '10m',
        },
        {
          value: 30,
          valueInSeconds: 900,
          label: '15m',
        },
        {
          value: 40,
          valueInSeconds: 1200,
          label: '20m',
        },
        {
          value: 50,
          valueInSeconds: 1800,
          label: '30m',
        },
        {
          value: 60,
          valueInSeconds: 2700,
          label: '45m',
        },
        {
          value: 70,
          valueInSeconds: 3600,
          label: '1h',
        },
        {
          value: 80,
          valueInSeconds: 5400,
          label: '1.5h',
        },
        {
          value: 90,
          valueInSeconds: 28800,
          label: '8h',
        },
      ],
    },
    [RESOLVE_KEY]: {
      id: 2,
      filterKey: RESOLVE_KEY,
      defaultValue: 20,
      min: 0,
      max: 40,
      step: null,
      primaryText: i18n.t('reports.conditionalEventsReport.resolutionTime'),
      marks: [
        {
          value: 0,
          valueInSeconds: 5,
          label: '5s',
        },
        {
          value: 10,
          valueInSeconds: 10,
          label: '10s',
        },
        {
          value: 20,
          valueInSeconds: 30,
          label: '30s',
        },
        {
          value: 30,
          valueInSeconds: 60,
          label: '1m',
        },
        {
          value: 40,
          valueInSeconds: 180,
          label: '3m',
        },
      ],
    },
  },
  [ConditionalEventType.OVER_SPEEDING]: {
    [MORE_THAN_VALUE_KEY]: {
      id: 1,
      filterKey: MORE_THAN_VALUE_KEY,
      defaultValue: 90,
      min: 50,
      max: 150,
      step: 5,
      primaryText: i18n.t('reports.conditionalEventsReport.speedAbove'),
      secondaryText: i18n.t('reports.conditionalEventsReport.kmh'),
    },
    [DURATION_KEY]: {
      id: 2,
      filterKey: DURATION_KEY,
      defaultValue: 30,
      step: 10,
      min: 0,
      max: 50,
      primaryText: i18n.t('reports.conditionalEventsReport.minDuration'),
      marks: [
        {
          value: 0,
          valueInSeconds: 5,
          label: '5s',
        },
        {
          value: 10,
          valueInSeconds: 10,
          label: '10s',
        },
        {
          value: 20,
          valueInSeconds: 30,
          label: '30s',
        },
        {
          value: 30,
          valueInSeconds: 60,
          label: '1m',
        },
        {
          value: 40,
          valueInSeconds: 120,
          label: '2m',
        },
        {
          value: 50,
          valueInSeconds: 300,
          label: '5m',
        },
      ],
    },
    [RESOLVE_KEY]: {
      id: 3,
      filterKey: RESOLVE_KEY,
      defaultValue: 30,
      min: 0,
      max: 40,
      step: 10,
      primaryText: i18n.t('reports.conditionalEventsReport.resolutionTime'),
      marks: [
        {
          value: 0,
          valueInSeconds: 5,
          label: '5s',
        },
        {
          value: 10,
          valueInSeconds: 10,
          label: '10s',
        },
        {
          value: 20,
          valueInSeconds: 30,
          label: '30s',
        },
        {
          value: 30,
          valueInSeconds: 60,
          label: '1m',
        },
        {
          value: 40,
          valueInSeconds: 180,
          label: '3m',
        },
      ],
    },
  },
  [ConditionalEventType.TEMPERATURE]: {},
  [ConditionalEventType.RPM_LIMIT]: {},
};
